import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CfStatusDisplayComponent } from '../cf-status-display/cf-status-display.component';
import { CfProfileComponent } from '../cf-profile/cf-profile.component';
import { CFButtonType, CfButtonComponent } from '../cf-button/cf-button.component';
import { CfUserProfileStackComponent } from '@ui/cf-user-profile-stack/cf-user-profile-stack.component';
import { CfTableCellComponent } from './cf-table-cell.component';

@Component({
    selector: 'app-cf-table',
    standalone: true,
    templateUrl: './cf-table.component.html',
    styleUrls: ['./cf-table.component.scss'],
    imports: [CommonModule, CfTableCellComponent]
})
export class CfTableComponent implements OnChanges {
    @Input() data!: CFTableData;
    @Input() interactive = true;
    @Input() filter?: CFTableFilter;
    @Input() sortable = false;
    @Input() showHeaders = true;
    @Input() showSelectionBox = false;
    @Input() profileSizePx = 50;
    @Input() profileBgColor?: string;
    @Input() profileTextColor?: string;

    @Output() onSelect: EventEmitter<CfTableSelectedRow> = new EventEmitter<CfTableSelectedRow>();
    @Output() onButtonClicked: EventEmitter<CFTableColumnButtonClicked> = new EventEmitter<CFTableColumnButtonClicked>();

    @Output() onItemChecked = new EventEmitter<number[]>();

    @Output() onRowAction = new EventEmitter<CFRowActionEvent>();

    initialised = false;

    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"] || changes["filter"]) {
            this.initialised = false;
            this.clearPageData();
            this.loadPageData();
        }
    }

    clearPageData(): void {
        const page = this.data.pages?.currentPage ?? 0;
        this.pages.set(page, new Set<number>());
        this.selectedRows.clear();
    }

    storePageData(): void {
        const page = this.data.pages?.currentPage ?? 0;
        this.pages.set(page, this.selectedRows);
    }

    loadPageData(): void {
        const cols = this.data.columns;
        if (this.data.rows.length === 0) {
            this.initialised = true;
            return;
        }

        const firstRow = this.data.rows[0];

        let columnLength = firstRow.cols.length;
        if (this.data.rowActions) columnLength += 1; // You don't supply all the columns for actions

        if (firstRow && cols.length !== columnLength) {
            console.error(`CfTableComponent: incorrect match of column definitions to passed rows: ${JSON.stringify(cols)} != ${JSON.stringify(firstRow.cols)}`);
            return;
        } else {
            this.displayFilteredAndSortedData();
        }

        // Set up data structures
        if (!this.initialised) {
            const maxPages = this.totalPages();
            for (let i = 0; i <= maxPages; i++) {
                this.pages.set(i, new Set<number>());
            }
            this.initialised = true;
        }

        const page = this.data.pages?.currentPage ?? 0;
        this.selectedRows = this.pages.get(page) ?? new Set<number>();
    }

    onClick(colIndex: number): void {
        if (!this.sortable) return;
        // console.log(`Try sorting on ${this.data.columns[colIndex].type}`);

        if (this.sortColumn === colIndex) {
            if (this.sortDirection === 'asc') {
                this.sortDirection = 'des';
            } else {
                this.sortDirection = 'asc';
            }
        } else {
            this.sortColumn = colIndex;
            this.sortDirection = 'asc';
        }

        this.displayFilteredAndSortedData();
    }

    // MARK: Actions
    rowActionClicked(actionRowIndex: number, tableRowIndex: number, rowPrimaryKey: string): void {
        //console.log(`RowActionClicked: ${actionRowIndex}:${rowPrimaryKey}`);
        if (this.data.rowActions) {
            const pk = this.data.rows[tableRowIndex].pk;
            const ra = this.data.rowActions[actionRowIndex];
            if (this.activeActionRow !== tableRowIndex) {
                this.activeActionRow = tableRowIndex;
                this.activeAction = actionRowIndex;

                const span = ra.columnSpan;

                // Adding a selection box shifts the user-defined columns 
                // over by 1
                let offset = 0;
                if (this.showSelectionBox) offset = 1;

                this.activeActionRowSpan = span;
                this.activeActionStartColumn = ra.startColumn;

                ra.active = true;
                // console.log(`RowAction (action_${this.activeAction}): start: ${this.activeActionStartColumn} span: ${this.activeActionRowSpan}`);

                this.onRowAction.emit(new CFRowActionEvent(ra, true, pk));

                this.isActive = true;
            }
        }
    }

    clearRowAction(tableRowIndex: number) {
        if (this.data.rowActions) {
            const ra = this.data.rowActions[this.activeActionRow];
            const pk = this.data.rows[tableRowIndex]?.pk;
            if (ra) {
                ra.active = false;
                this.onRowAction.emit(new CFRowActionEvent(ra, false, pk))
            } else {
                console.warn(`Ra is null, with activeActionRow ${this.activeActionRow}`);
            }
        }
        this.activeActionRow = -1;
        this.activeActionRowSpan = 1;
        this.activeActionStartColumn = 100;
        this.activeAction = -1;

        this.isActive = false;
    }

    activeActionRow = -1;
    activeAction = -1;
    activeActionStartColumn = 100;
    activeActionRowSpan = 1;

    // MARK: Selection Box events
    selectedRows = new Set<number>();
    isAllSelected = false;
    pages = new Map<number, Set<number>>();
    selectAll(): void {
        this.clearPageData();

        this.isAllSelected = !this.isAllSelected;
        if (this.isAllSelected) {
            const pageSize = this.data.pages?.pageSize ?? 0;
            const max = this.totalPages();

            for (let p = 0; p <= max; p++) {
                const s = new Set<number>();
                for (let i = 0; i < pageSize; i++) {
                    s.add(i);
                }
                this.pages.set(p, s);
            }

            for (let i = 0; i < this.data.rows.length; i++) {
                this.selectedRows.add(i);
            }
        } else {
            const maxPages = this.totalPages();
            for (let i = 0; i <= maxPages; i++) {
                this.pages.set(i, new Set<number>());
            }
            this.selectedRows.clear();
        }

        // Fire event;
        this.sendSelectedIndices();
    }

    selectIndividualRow(event: Event, row: number): void {
        let isSelected = this.selectedRows.has(row);
        if (isSelected) {
            // currently selected; remove
            this.selectedRows.delete(row);
        } else {
            // currently unselected; add
            this.selectedRows.add(row);
        }

        if (this.selectedRows.entries.length !== this.data.rows.length) {
            this.isAllSelected = false;
        }

        event.stopImmediatePropagation();

        this.sendSelectedIndices();
    }

    private sendSelectedIndices(): void {
        this.onItemChecked.emit(this.getAllSelections());
    }

    private getAllSelections(): number[] {
        let r: number[] = [];

        this.storePageData();

        // convert page rows into canoninical rows

        const pageSize = this.data.pages?.pageSize ?? 0;
        const max = this.totalPages();

        for (let p = 0; p <= max; p++) {
            //console.log(`P: ${p}`);
            const s = this.pages.get(p);
            if (s) {
                s.forEach((se: number) => {
                    let actualIndex = se + (pageSize * p);
                    ///console.log(`P: ${p}, SE: ${se} - actual idx: ${actualIndex}`);
                    r.push(actualIndex);
                });
            }
        }

        return r;
    }

    // MARK: Display/Filtering/Sorting
    displayFilteredAndSortedData(): void {
        // Take the 'raw' data and populate the 'displayRows'
        // If we are paginated, sorted, or filtered, the 
        // displayRows will be markedly different
        let rows: CFTableRow[] = [];

        let filteredData = this.data.rows.slice();

        // filtering first, because it should be the easiest
        if (this.filter) {
            if (this.filter.column < this.data.columns.length) {
                const fc = this.filter.column;
                const fv = this.filter.value;
                filteredData = this.data.rows.filter(f => {
                    return f.cols[fc] === fv;
                });
            }
        }

        // next, are we sorting?
        if (this.sortable && this.sortColumn > -1) {
            rows = this.sortData(filteredData);
        } else {
            rows = filteredData;
        }

        // finally, are we paginating?
        if (this.data.pages) {
            const p = this.data.pages;

            const startIndex = p.currentPage * p.pageSize;
            const endIndex = startIndex + p.pageSize;

            //console.log(`Page: ${p.currentPage} = Start ${startIndex}, End ${endIndex}`);

            this.displayRows = rows.slice(startIndex, endIndex);//this.data.rows.slice(startIndex, endIndex);

            this.updateButtons(this.data.pages);
        } else {
            // Display all; no pagination
            this.displayRows = rows; //this.data.rows;
        }

    }

    sortData(source: CFTableRow[]): CFTableRow[] {
        const sorted = source.sort((a, b) => {
            const type = this.data.columns[this.sortColumn].type;
            const ac = this.sortDirection === 'asc' ? a.cols[this.sortColumn] : b.cols[this.sortColumn];
            const bc = this.sortDirection === 'asc' ? b.cols[this.sortColumn] : a.cols[this.sortColumn];
            switch (type) {
                case CfTableColumnType.String:
                case CfTableColumnType.UserStatus:
                    return ac ? ac.localeCompare(bc, 'en') : 0;
                case CfTableColumnType.UserProfile:
                    return ac ? ac.localeCompare(bc ?? '', 'en') : 0;
                case CfTableColumnType.UserProfiles:
                    const nameA = `${ac.firstName} ${ac.lastName}`;
                    const nameB = `${bc.firstName} ${bc.lastName}`;
                    return nameA && nameB ? nameA.localeCompare(nameB, 'en') : 0;
                case CfTableColumnType.Date:
                    const parseDate = (v: string): number => {
                        if (!v || v === 'Never') return 0;
                        const parts = v.split('/');
                        if (parts.length === 3) return new Date(+parts[2], +parts[1] - 1, +parts[0]).getTime();
                        const t = new Date(v).getTime();
                        return isNaN(t) ? 0 : t;
                    };
                    return parseDate(ac) - parseDate(bc);
                case CfTableColumnType.Number:
                case CfTableColumnType.Boolean:
                    console.log(`Sort on Number/Bool`);
                    return parseInt(ac) - parseInt(bc);
                case CfTableColumnType.Icon:
                    const ai = ac ? 1 : 0;
                    const bi = bc ? 1 : 0;
                    return ai - bi;
            }
            return 0;
        });
        return sorted.slice();
    }

    selectRow(rowIndex: number): void {
        // console.log(`Selected row: ${rowIndex}: ${JSON.stringify(this.displayRows[rowIndex])}`);
        this.onSelect.emit({ pk: this.displayRows[rowIndex].pk, index: rowIndex });
    }

    buttonClicked(text: string, pk: string): void {
        // console.log(`row button clicked:`, text, pk);
        this.onButtonClicked.emit(new CFTableColumnButtonClicked(text, pk));
    }

    setFirstPage(): void {
        this.storePageData();

        if (this.data.pages) {
            this.data.pages!.currentPage = 0;
            this.loadPageData();
        }
    }

    nextPage(): void {
        this.storePageData();

        if (this.data.pages) {
            this.data.pages!.currentPage += 1;
            if (this.data.pages!.currentPage == this.data.pages!.pageCount) {
                this.data.pages!.currentPage = this.data.pages!.pageCount;
            }
            this.loadPageData();
        }
    }

    previousPage(): void {
        this.storePageData();

        if (this.data.pages) {
            this.data.pages!.currentPage -= 1;
            if (this.data.pages!.currentPage < 0) {
                this.data.pages!.currentPage = 0;
            }
            this.loadPageData();
        }
    }

    setLastPage(): void {
        this.storePageData();

        if (this.data.pages) {
            const maxPages = this.totalPages();
            this.data.pages!.currentPage = maxPages;
            this.loadPageData();
        }
    }

    updateButtons(pages: CFTablePaginationOptions): void {
        const maxPages = this.totalPages();

        // console.log(`Current: ${pages.currentPage}, maxPages: ${maxPages}`);

        this.df = !(pages.currentPage >= 1);
        this.dp = !(pages.currentPage >= 1);
        this.dn = !(pages.currentPage < maxPages);
        this.dl = !(pages.currentPage < maxPages);

        // console.log(`Disabled: F/P/N/L: ${this.df}/${this.dp}/${this.dn}/${this.dl}`);
    }

    goToPage(pageIndex: number): void {
        this.storePageData();

        if (this.data.pages) {
            const maxPages = this.totalPages();
            this.data.pages!.currentPage = Math.max(0, Math.min(pageIndex, maxPages));
            this.loadPageData();
        }
    }

    getPageNumbers(): (number | string)[] {
        if (!this.data.pages) return [];

        const currentPage = this.data.pages.currentPage;
        const totalPages = this.data.pages.pageCount;

        if (totalPages <= 7) {
            // Show all pages if 7 or fewer
            return Array.from({ length: totalPages }, (_, i) => i);
        }

        const pages: (number | string)[] = [];

        // Always show first page
        pages.push(0);

        if (currentPage > 2) {
            pages.push('...');
        }

        // Show pages around current page
        const start = Math.max(1, currentPage - 1);
        const end = Math.min(totalPages - 2, currentPage + 1);

        for (let i = start; i <= end; i++) {
            if (!pages.includes(i)) {
                pages.push(i);
            }
        }

        if (currentPage < totalPages - 3) {
            pages.push('...');
        }

        // Always show last page
        if (!pages.includes(totalPages - 1)) {
            pages.push(totalPages - 1);
        }

        return pages;
    }

    isActive = false;
    overlayClicked() {
        this.clearRowAction(this.activeActionRow);
    }

    private totalPages(): number {
        if (this.data.pages) {
            const pageSize = this.data.pages.pageSize > 0 ? this.data.pages.pageSize : 1;
            return Math.ceil(this.data.rows.length / pageSize) - 1;
        }
        return 1;
    }

    displayRows: CFTableRow[] = [];

    df = false;
    dp = false;
    dn = false;
    dl = false;

    sortColumn = -1;
    sortDirection = 'asc';
}

export interface CfTableSelectedRow {
    index: number;
    pk: string;
}

export enum CfTableColumnAlignment {
    Left = "lal",
    Center = "cal",
    Right = "ral"
}

export enum CfTableColumnType {
    Any = "*",
    String = "string",
    Number = "number",
    Boolean = "boolean",
    Date = "date",
    UserProfile = "profile",
    UserProfiles = "profiles",
    UserStatus = "status",
    Button = "button",
    ActionList = "actions",
    Icon = "icon",
}

export interface ICFRowActionOptions {
    rowActions: ICFRowAction[];
}

export interface ICFRowAction {
    text: string;
    active: boolean;
    startColumn: number;
    columnSpan: number;
}

export interface ICFTableColumnDefinition {
    title?: string;
    align: CfTableColumnAlignment;
    type: CfTableColumnType;
    class?: string;
    cellClass?: string;
    getClass(): any;

    options?: undefined | ICFTableColumnTypeOptions;
}

export interface ICFTableColumnTypeOptions {
    button?: ICFTableColumnButtonOptions;
}

export interface ICFTableColumnButtonOptions {
    type: CFButtonType;
}

export interface ICFTableRow {
    pk: string;
    cols: any[];
    canSelect?: boolean;
}

export interface ICFTablePaginationOptions {
    pageSize: number;
    currentPage: number;
    pageCount: number;
}

export class CFTableData {
    columns: ICFTableColumnDefinition[];
    rows: ICFTableRow[];
    pages?: ICFTablePaginationOptions;
    rowActions?: ICFRowAction[];

    constructor(c: ICFTableColumnDefinition[], r: ICFTableRow[], paginationOptions?: ICFTablePaginationOptions, actionOptions?: ICFRowActionOptions) {
        this.rows = r;
        this.pages = paginationOptions ?? undefined

        if (actionOptions) {
            // FAILSAFE
            if (!actionOptions.rowActions) console.error(`You have specified an action row without supplying details in the rowActions array`);

            this.rowActions = actionOptions.rowActions;

            // If we have row actions, we should create an additional column to surface the options
            c.push(new CFTableColumn("Actions", CfTableColumnAlignment.Center, CfTableColumnType.ActionList, "col_actions"));
        }

        this.columns = c;
    }
}

export class CFTableColumn implements ICFTableColumnDefinition {
    title?: string;
    align: CfTableColumnAlignment = CfTableColumnAlignment.Left;
    type: CfTableColumnType = CfTableColumnType.Any;
    class?: string;
    cellClass?: string;

    options?: undefined | ICFTableColumnTypeOptions;

    constructor(title?: string, alignment = CfTableColumnAlignment.Left, type = CfTableColumnType.Any, cssClass?: string, cssCellClass?: string, options?: ICFTableColumnTypeOptions) {
        this.title = title ?? '';
        this.align = alignment;
        this.type = type;
        this.class = cssClass;
        this.cellClass = cssCellClass || '';
        this.options = options;
    }

    getClass(): any {
        return `${this.align} ${this.class}`
    }
}

export class CFTableRow implements ICFTableRow {
    pk: string;
    cols: any[] = [];
    tooltips?: string[];
    canSelect?: boolean = true;
    constructor(pk: string, columns: any[], canSelect: boolean = true, tooltips?: string[]) {
        this.pk = pk;
        this.cols = columns;
        this.canSelect = canSelect;
        this.tooltips = tooltips;
    }
}

export class CFTablePaginationOptions {
    pageSize: number;
    currentPage: number;
    pageCount: number;

    constructor(size: number, rowCount: number) {
        this.pageSize = size;
        this.currentPage = 0;
        if (size > 0) {
            this.pageCount = Math.ceil(rowCount / size);
        } else {
            this.pageCount = 0;
        }
    }
}
export class CFRowActionOptions implements ICFRowActionOptions {
    rowActions: ICFRowAction[] = [];
    constructor(actions: ICFRowAction[]) {
        this.rowActions = actions;
    }
}

export class CFRowAction implements ICFRowAction {
    text: string;
    active: boolean;
    startColumn: number;
    columnSpan: number;
    constructor(text: string, start: number, span: number) {
        this.active = false;
        this.text = text;
        this.startColumn = start;
        this.columnSpan = span;
    }
}


export interface CFTableFilter {
    column: number;
    value: string | number | boolean; // convert case as needed
}

export class CFTableColumnButtonClicked {
    text: string;
    pk: string;
    constructor(text: string, pk: string) {
        this.text = text;
        this.pk = pk;
    }
}

export class CFRowActionEvent {
    isShowing: boolean = false;
    isHiding: boolean = false;
    action: CFRowAction;
    rowPrimaryKey: string;
    constructor(action: CFRowAction, isShowing: boolean, pk: string) {
        this.action = action;
        this.rowPrimaryKey = pk;
        this.isShowing = isShowing;
        this.isHiding = !isShowing;
    }
}